/* eslint-disable @typescript-eslint/no-explicit-any */

import { Key } from 'react';
import { Pagination } from '@stack/ui/components';
import classNames from 'classnames';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';

import { months } from './constant';
import {
  PaginationWrapper,
  StyledFilterContainer,
  StyledNoData,
  StyledNoDataBox,
  StyledResultBox,
  StyledResultsContainer,
  StyledSelectedYear,
  StyledSitemapFilterOpts,
  StyledSitemapFilterTitle,
  StyledTable,
  StyledTableData,
  StyledTableDataAnchor,
  StyledTableHeader,
  StyledTableRow,
  StyledTotalSpan,
  StyledYearOpts,
} from './styles';

/**
 * ===========================
 * MAIN
 * ===========================
 */
const SitemapScreen = (props: any) => {
  const { sitemapSettings, sitemaps, currentPage, loading, href } = props;
  const years = sitemapSettings?.year ?? ['2024'];
  const tags = sitemapSettings?.tag ?? [];
  const datas = sitemaps?.data ?? [];
  const totals = sitemaps?.total ?? 0;

  const { t } = useTranslation('common');
  const lang = 'en';
  const router = useRouter();
  const { query } = router;
  const yearQuery = (query?.year as string) ?? null;
  const monthQuery = (query?.month as string) ?? null;
  const tagQuery = (query?.tag as string) ?? null;
  const totalCount = sitemaps?.total ?? 0;
  const pageSize = sitemaps?.per_page ?? 10;

  return (
    <>
      <StyledFilterContainer>
        <StyledSitemapFilterTitle>{t('searchByYear')}</StyledSitemapFilterTitle>
        <StyledSitemapFilterOpts>
          {years?.map((year: number) => {
            return (
              <Link key={year} href={`/browse-by-year/${year}`}>
                <div
                  className={classNames('option-style', {
                    active: parseInt(yearQuery) === year,
                  })}
                >
                  <StyledYearOpts>{year}</StyledYearOpts>
                </div>
              </Link>
            );
          })}
        </StyledSitemapFilterOpts>
        <StyledSitemapFilterTitle
          style={{
            paddingTop: '1.5rem',
          }}
        >
          {t('searchByTag')}
        </StyledSitemapFilterTitle>
        <StyledSitemapFilterOpts>
          {tags?.map((tag: any) => {
            return (
              <Link
                key={tag?.post_tag_id}
                href={`/browse-by-year/tag/${tag?.post_tag_slug}`}
              >
                <div
                  className={classNames('tag-style', {
                    active: tagQuery === tag.post_tag_slug,
                  })}
                >
                  {tag?.post_tag_name[lang]}
                </div>
              </Link>
            );
          })}
        </StyledSitemapFilterOpts>
      </StyledFilterContainer>

      <StyledResultsContainer>
        {yearQuery && (
          <div
            style={{
              marginBottom: '1rem',
            }}
          >
            <StyledSelectedYear>{yearQuery}</StyledSelectedYear>
            <StyledSitemapFilterOpts>
              <Link href={`/browse-by-year/${yearQuery}`}>
                <div
                  className={classNames('option-style', {
                    active: !monthQuery,
                  })}
                  style={{
                    padding: '6px 20px',
                  }}
                >
                  <StyledYearOpts>{t('all')}</StyledYearOpts>
                </div>
              </Link>
              {months?.map((mth) => {
                return (
                  <Link
                    key={mth.value}
                    href={`/browse-by-year/${yearQuery}?month=${mth.value}`}
                  >
                    <div
                      className={classNames('option-style', {
                        active: parseInt(monthQuery) === mth.value,
                      })}
                      style={{
                        padding: '6px 20px',
                      }}
                    >
                      <StyledYearOpts>{mth.label}</StyledYearOpts>
                    </div>
                  </Link>
                );
              })}
            </StyledSitemapFilterOpts>
          </div>
        )}
        <StyledTotalSpan>{t('TotalNewsFound', { totals })}</StyledTotalSpan>
        <StyledResultBox>
          <StyledTable>
            <thead>
              <tr>
                <StyledTableHeader className="table-width-15">
                  Date
                </StyledTableHeader>
                <StyledTableHeader>Title</StyledTableHeader>
              </tr>
            </thead>
            <tbody>
              {datas.map((data: any, index: Key) => {
                return (
                  <StyledTableRow key={index}>
                    <StyledTableData
                      style={{
                        maxWidth: '100px',
                      }}
                    >
                      <time
                        className={classNames('', {
                          'skeleton skeleton-title-text': loading,
                        })}
                        dateTime={data.post_date}
                      >
                        {data.post_date.split(' ')[0]}
                      </time>
                    </StyledTableData>
                    <StyledTableData>
                      {loading ? (
                        <div
                          className="skeleton skeleton-title-text"
                          style={{
                            height: '0.813rem',
                            minWidth: '100%',
                          }}
                        />
                      ) : (
                        <StyledTableDataAnchor href={data?.post_url}>
                          {data.post_title}
                        </StyledTableDataAnchor>
                      )}
                    </StyledTableData>
                  </StyledTableRow>
                );
              })}
            </tbody>
          </StyledTable>
          {datas.length < 1 && (
            <StyledNoData>
              <StyledNoDataBox>
                <p>{t('noData')}</p>
              </StyledNoDataBox>
            </StyledNoData>
          )}
        </StyledResultBox>
        <PaginationWrapper>
          <Pagination
            className="pagination-bar"
            siblingCount={1}
            href={href}
            totalCount={totalCount}
            currentPage={currentPage}
            pageSize={pageSize}
            hasQuery={!!monthQuery}
          />
        </PaginationWrapper>
      </StyledResultsContainer>
    </>
  );
};

/**
 * ===========================
 * EXPORTS
 * ===========================
 */
export default SitemapScreen;
